import React, { useCallback, useEffect, useState } from 'react';
import { getSortState, translate, Translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';

import { Box } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import { getEntities, reset } from './fifth-wheel-connection.reducer';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';

export const FifthWheelConnection = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(
      {
        ...getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'created'),
        order: DESC,
      },
      props.location.search
    )
  );

  const fifthWheelConnectionList = useAppSelector(state => state.fifthWheelConnection.entities);
  const loading = useAppSelector(state => state.fifthWheelConnection.loading);
  const totalItems = useAppSelector(state => state.fifthWheelConnection.totalItems);
  const [truckImplementSearch, setTruckImplementSearch] = useState<string>('');
  const [dateSearch, setDateSearch] = useState<string>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);

  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const [companySearch, setCompanySearch] = useState<string>('');

  const headers: IHeaderItem[] = [
    { key: 'created', sortable: true, component: <Translate contentKey="fleetSenseApp.fifthWheelConnection.created" /> },
    { key: 'vehicle.licensePlate', sortable: true, component: <Translate contentKey="fleetSenseApp.fifthWheelConnection.vehicle" /> },
    { key: 'trailer.licensePlate', sortable: true, component: <Translate contentKey="fleetSenseApp.fifthWheelConnection.trailer" /> },
    { key: 'company.name', sortable: true, component: <Translate contentKey="fleetSenseApp.fifthWheelConnection.company" /> },
  ];
  const filteredHeaders = !isAdmin ? headers.filter(header => header.key !== 'company.name') : headers;

  const data: IListData[] = [
    { type: 'convertDateTimeToPtBR', value: 'created' },
    { type: 'object', value: 'vehicle', label: 'licensePlate' },
    { type: 'object', value: 'trailer', label: 'licensePlate' },
    { type: 'object', value: 'company', label: 'name', onlyAdmin: true },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(reset());
    dispatch(
      getEntities({
        companyId,
        truckImplementSearch,
        dateSearch,
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  }, [paginationState]);

  const sortEntities = () => {
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (event, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const searchTypes: ISearchTypes[] = [
    {
      value: truckImplementSearch,
      setValue: setTruckImplementSearch,
      placeholder: translate('fleetSenseApp.fifthWheelConnection.truckImplementSearch'),
      label: translate('fleetSenseApp.fifthWheelConnection.vehicle'),
    },
  ];

  const handleSearchCompany = (company: { id: number; name: string }) => {
    if (isAdmin) {
      setCompanyId(company.id);
      setCompanySearch(company.name);
      setActivePage();
    }
  };

  const handleDateSearch = (date: string) => {
    setDateSearch(date);
    setActivePage();
  };

  const sortClear = () => {
    setCompanyId(null);
    setCompanySearch('');
    setDateSearch(null);
    setTruckImplementSearch('');
    setActivePage();
  };

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', padding: '24px' }}>
      <PageHeader
        headingId="fifth-wheel-connection-heading"
        headingDataCy="FifthWheelConnectionHeading"
        contentKey="fleetSenseApp.fifthWheelConnection.home.title"
        buttonTextKey="fleetSenseApp.fifthWheelConnection.home.createLabel"
        match={match}
        showButton={false}
      />

      <PageFilters
        setActivePage={setActivePage}
        searchTypes={searchTypes}
        isAdmin={isAdmin}
        dateSearch={dateSearch}
        handleDateSearch={handleDateSearch}
        handleSearchCompany={handleSearchCompany}
        searchCompanyName={companySearch}
        sortClear={sortClear}
        showFilters={['filters', 'date', 'company', 'clearFilters']}
      />
      {!loading && (
        <TableComponent
          listData={data}
          list={fifthWheelConnectionList}
          headers={filteredHeaders}
          match={match}
          sortFunction={sort}
          pagination={paginationState}
        />
      )}
      {!loading && !fifthWheelConnectionList.length && <NoData title="conexão de quinta roda" />}
      {loading && <Loading />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={fifthWheelConnectionList} />
    </Box>
  );
};

export default FifthWheelConnection;
